import React from "react";
import * as ExcelJS from "exceljs";
import saveAs from "file-saver";
import { toast, ToastContainer } from "react-toastify";
import { Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Converts an API field key (e.g. "brandTypeName", "isAVInstalled") into a
// readable header label (e.g. "Brand Type Name", "Is AV Installed").
function fieldLabelFromKey(key) {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

// Fields that should always appear in the export, even if every row in the
// current page happens to have them null/empty (so the column never
// silently disappears).
const ALWAYS_INCLUDE_KEYS = ["remarks"];

// E-Waste status codes -> display labels
const EWASTE_STATUS_LABELS = {
  0: "Pending",
  1: "In Company",
  2: "Disposed",
};

function ExcelExportButton({ data, asset }) {
  const customId = "custom-id-yes";

  let apiData = data?.map((value) => {
    const clone = { ...value };
    if (clone?.createdOn?.length > 0) {
      clone.createdOn = clone.createdOn.split("T")[0];
    }
    if (clone?.purchaseDate?.length > 0) {
      clone.purchaseDate = clone.purchaseDate.split("T")[0];
    }
    if (clone?.warrantyTill?.length > 0) {
      clone.warrantyTill = clone.warrantyTill.split("T")[0];
    }
    const { id, isDelete, changeInRemarks, ...rest } = clone;
    if (asset !== "RentalLaptop") {
      delete rest.graphicCard;
    }
    return rest;
  });

  const handleExportClick = () => {
    if (data?.length === 0) {
      toast.error("No data is there to export", {
        toastId: customId,
      });
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Data");

    const headerStyle = {
      font: {
        name: "Arial",
        bold: true,
        color: { argb: "FFFFFF" },
        size: 10,
      },
      alignment: {
        horizontal: "center",
        vertical: "middle",
        wrapText: false,
      },
      fill: {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "4f81bd" },
      },
    };

    const dataCellStyle = {
      alignment: {
        horizontal: "left",
        vertical: "middle",
        wrapText: false,
      },
      border: {
        top: { style: "thin", color: { argb: "0000" } },
        left: { style: "thin", color: { argb: "0000" } },
        bottom: { style: "thin", color: { argb: "0000" } },
        right: { style: "thin", color: { argb: "0000" } },
      },
    };

    // Remarks can be long free text — wrap instead of stretching the column.
    const remarksCellStyle = {
      ...dataCellStyle,
      alignment: {
        ...dataCellStyle.alignment,
        wrapText: true,
      },
    };

    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow([]);

    const headingStyle = {
      font: {
        name: "Arial",
        bold: true,
        color: { argb: "0000" },
        size: 15,
      },
      alignment: {
        horizontal: "center",
        vertical: "middle",
        wrapText: false,
      },
      border: {
        top: { style: "thin", color: { argb: "0000" } },
        left: { style: "thin", color: { argb: "0000" } },
        bottom: { style: "thin", color: { argb: "0000" } },
        right: { style: "thin", color: { argb: "0000" } },
      },
    };

    // for styling main heading
    worksheet.mergeCells("E1", "H2");
    const cellsToStyle = ["E1", "E2", "F1", "F2", "G1", "G2", "H1", "H2"];
    if (asset === "CPU") {
      worksheet.getCell("E1").value = " Desktop's Informations";
    } else {
      worksheet.getCell("E1").value = `${asset}'s Informations`;
    }

    cellsToStyle.forEach((cells) => {
      const cell = worksheet.getCell(cells);
      cell.style = headingStyle;
    });

    // --- Build headers dynamically from whatever the API actually returned ---
    // Uses the union of keys across all rows (in case some rows omit fields),
    // preserving first-seen order so column order stays stable.
    const orderedKeys = [];
    const seen = new Set();
    apiData.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (!seen.has(key)) {
          seen.add(key);
          orderedKeys.push(key);
        }
      });
    });

    // Guarantee important columns (e.g. Remarks) are always present, even if
    // every row in the current dataset happens to omit that key.
    ALWAYS_INCLUDE_KEYS.forEach((key) => {
      if (!seen.has(key)) {
        seen.add(key);
        orderedKeys.push(key);
      }
    });

    const LABEL_OVERRIDES = {
      graphicCard: "Graphic Card(GB)",
    };

    const headings = orderedKeys.map(
      (key) => LABEL_OVERRIDES[key] ?? fieldLabelFromKey(key)
    );

    if (asset === "RentalLaptop") {
      if (!seen.has("graphicCard")) {
        seen.add("graphicCard");
        orderedKeys.push("graphicCard");
      }
      // move it directly after RAM
      orderedKeys.splice(orderedKeys.indexOf("graphicCard"), 1);
      const ramIdx = orderedKeys.indexOf("ram");
      orderedKeys.splice(
        ramIdx === -1 ? orderedKeys.length : ramIdx + 1,
        0,
        "graphicCard"
      );
    }

    const headerRow = worksheet.addRow(headings);
    headerRow.eachCell((cell) => {
      cell.style = headerStyle;
    });

    apiData.forEach((item) => {
      const row = worksheet.addRow([]);

      orderedKeys.forEach((key, index) => {
        let value = item[key];

        // Map numeric E-Waste status codes to readable labels
        if (key === "eWasteStatus" && value !== null && value !== undefined && value !== "") {
          value = EWASTE_STATUS_LABELS[value] ?? value;
        }

        const cell = row.getCell(index + 1);
        if (value !== null && value !== undefined && value !== "") {
          cell.value = value;
        }
        cell.style = key === "remarks" ? remarksCellStyle : dataCellStyle;
      });
    });

    // Auto-size columns to fit content, but cap the Remarks column so a
    // single very long note doesn't blow out the whole sheet width.
    worksheet.columns.forEach((column, colIndex) => {
      const key = orderedKeys[colIndex];
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const textLength = cell.value ? cell.value.toString()?.length : 0;
        if (textLength > maxLength) {
          maxLength = textLength;
        }
      });

      if (key === "remarks") {
        column.width = 40; // fixed, comfortable width; wrapText handles overflow
      } else {
        column.width = maxLength < 10 ? 10 : maxLength + 2;
      }
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      saveAs(
        new Blob([buffer], { type: "application/octet-stream" }),
        `${asset === "CPU" ? "Desktop" : asset}'s Data.xlsx`
      );
    });
  };

  return (
    <div>
      {/* <ToastContainer transition={Slide} /> */}
      <button
        className="btn btn-primary flex justify-center items-center min-w-80  cursor-pointer !bg-[#22c55e] !border-[#22c55e]"
        onClick={handleExportClick}
      >
        Export
      </button>
    </div>
  );
}
export default ExcelExportButton;